import {Component, inject, OnInit} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatButton} from '@angular/material/button'
import {MatOption} from '@angular/material/core'
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog'
import {MatFormField, MatLabel} from '@angular/material/form-field'
import {MatSelect} from '@angular/material/select'
import {APPLICATION_ID, IApplication, IApplicationAdminData, IRegistration} from '@sparbanken-syd/loan-backend'
import {WaitComponent} from '@sparbanken-syd/sparbanken-syd-theme'
import {LoanService} from '../../../services/loan.service'
import {RegisterService} from '../../../services/register.service'

/**
 * Type guard to check if the loanPromise is a registration.
 */
const isRegistration = (loanPromise: IApplication | IRegistration): loanPromise is IRegistration =>
  loanPromise.status === 'registered'

@Component({
  selector: 'spb-delegate',
  templateUrl: './delegate.component.html',
  styleUrls: ['./delegate.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatDialogActions,
    MatButton,
    MatDialogClose
  ]
})
export class DelegateComponent implements OnInit {
  public readonly dialogRef = inject<MatDialogRef<DelegateComponent>>(MatDialogRef)

  public readonly data = inject<{
    application: IApplication
    offices: string[]
  }>(MAT_DIALOG_DATA)

  /**
   * Input value from callee, set through data
   */
  public currentAssigneeValue: string | undefined
  public currentOfficeValue: string | undefined
  public phone: string | undefined = undefined
  public owner: string | undefined = undefined

  /**
   * Form controls that holds data for delegate and office.
   */
  public selectDelegate = new FormControl<string>('')
  public selectOffice = new FormControl<string>('')
  protected offices: string[] = []
  protected readonly loanService = inject(LoanService)
  private readonly registerService = inject(RegisterService)
  private readonly dialog = inject(MatDialog)

  public ngOnInit(): void {
    this.initializeFormControls()
    this.setInitialFormValues()
  }

  /**
   * Saves the changes by updating the admin data of the application.
   * Depending on the application type, it updates the assignee and closes the dialog.
   */
  public save(): void {
    const newAdminData: IApplicationAdminData = this.createAdminData()

    // Ensure local data is updated before making API calls
    this.data.application = {...this.data.application, adminData: newAdminData}

    if (isRegistration(this.data.application)) {
      this.updateRegisteredApplication(newAdminData)
    } else {
      this.updateLoanApplication(newAdminData)
    }
  }

  /**
   * Sets up form controls for delegate selection and reacts to changes in value.
   */
  private initializeFormControls(): void {
    this.selectDelegate.valueChanges.subscribe((userName: string | null) => {
      this.updateAdminData(userName)
    })
  }

  /**
   * Initializes the office and delegate form values based on the provided application data.
   */
  private setInitialFormValues(): void {
    this.offices = this.data.offices
    this.selectDelegate.setValue(this.data.application.adminData?.assignee ?? '')
    this.selectOffice.setValue(this.data.application.adminData?.office ?? '')
    this.currentAssigneeValue = this.data.application.adminData?.assignee
    this.currentOfficeValue = this.data.application.adminData?.office
  }

  /**
   * Updates the office and phone fields based on the selected delegate's username.
   *
   * @param userName - The selected delegate's username.
   */
  private updateAdminData(userName: string | null): void {

    if (!userName) {
      this.phone = ''
      this.owner = ''
      return
    }

    const user = this.loanService.users().find(user => user.name === userName)

    const office = user?.office ?? null
    this.phone = user?.phone ?? undefined
    this.owner = user?.sId ?? undefined
    this.selectOffice.setValue(office)
  }

  /**
   * Creates a new IApplicationAdmin object with updated assignee, office, and phone values.
   * It falls back to existing values if the new values are empty.
   *
   * @returns The updated IApplicationAdmin object.
   */
  private createAdminData(): IApplicationAdminData {
    return {
      assignee: this.selectDelegate.value ?? this.data.application.adminData?.assignee,
      office: this.selectOffice.value ?? this.data.application.adminData?.office,
      phone: this.phone,
      owner: this.owner
    }
  }

  /**
   * Updates the admin data for registered applications.
   *
   * @param newAdminData - The updated admin data.
   */
  private updateRegisteredApplication(newAdminData: IApplicationAdminData): void {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Uppdaterar...', progress: true}
    })
    const application = this.registerService.registeredPromises$.value.find(
      a => a[APPLICATION_ID] === this.data.application[APPLICATION_ID]
    )!

    application.adminData = newAdminData as any
    this.registerService.updateRegistered(application).subscribe({
      next: () => {
        waitRef.close()
        this.dialogRef.close(true)
      }
    })

  }

  /**
   * Updates the admin data for loan applications and fetches the latest registrations.
   * First updates the assignee, then retrieves the current registrations
   */
  private updateLoanApplication(newAdminData: IApplicationAdminData): void {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Uppdaterar', progress: true}
    })
    this.loanService.updateAssignee(this.data.application['loan-promise-id'], newAdminData)
      .pipe(
        //switchMap(() => this.registerService.getRegistrations())
      )
      .subscribe({
        next: () => {
          waitRef.close()
          this.dialogRef.close(true)
        }
      })
  }
}
